import { ref, watch, type WatchStopHandle } from 'vue'

import type { CreativeLabellingProgressEvent } from '../models/labelling.types'
import {
  classifyLabellingFrame,
  isNewerLabellingFrame,
  isTerminalLabellingFrame,
  parseLabellingFrame,
} from '../service/labellingFrame'
import type { CreativeAiStore } from '../stores/creativeAiStore'
import { useCreativeAiStore } from '../stores/creativeAiStore'
import { useCreativeAiViewStore } from '../stores/creativeAiViewStore'

import { useAccountSession } from '@/features/account'
import { useCreditGuard } from '@/features/credits'

export type CreativeAiProgressEventName =
  'creative_labelling_progress' | 'creative_brief_progress' | 'stream.gap'

export interface CreativeAiProgressEventBus {
  connected?: Readonly<{ value: boolean }>
  on: (name: CreativeAiProgressEventName, handler: (payload: unknown) => void) => () => void
}

interface VisibilityDocument {
  readonly visibilityState: DocumentVisibilityState
  addEventListener: Document['addEventListener']
  removeEventListener: Document['removeEventListener']
}

export interface RelabelProgressConsumer {
  active: Readonly<{ value: boolean }>
  applyProgress: (frame: CreativeLabellingProgressEvent) => void
  reload: () => Promise<void>
  reset: () => void
}

export interface LabellingProgressRuntimeDeps {
  bus: CreativeAiProgressEventBus
  workspaceId: () => number | string | undefined
  isAnalysisActive: () => boolean
  isRelabelActive: () => boolean
  applyAnalysisProgress: (frame: CreativeLabellingProgressEvent) => void
  applyRelabelProgress: (frame: CreativeLabellingProgressEvent) => void
  reloadAnalyses: () => Promise<void>
  reloadRelabel: () => Promise<void>
  refreshBalance: () => Promise<void>
  document?: VisibilityDocument
}

export interface LabellingProgressRuntime {
  start: () => void
  stop: () => void
  whenIdle: () => Promise<void>
}

let installedBus: CreativeAiProgressEventBus | null = null
let relabelConsumer: RelabelProgressConsumer | null = null
const relabelRegistrationVersion = ref(0)
let installedLifecycleStops: WatchStopHandle[] = []
let installedRuntime: LabellingProgressRuntime | null = null

/** Composition-root injection keeps the feature island independent of the shell. */
export function registerCreativeAiEventBus(bus: CreativeAiProgressEventBus): void {
  installedBus = bus
}

export function creativeAiStreamConnected(): boolean {
  return installedBus?.connected?.value ?? false
}

/**
 * The one shell bus this feature ever registers (`registerCreativeAiEventBus`, bootstrap-time) —
 * reused by sibling SSE consumers within the feature (e.g. `useCreativeBrief`) so a second event
 * name doesn't need a second composition-root registration call.
 */
export function getCreativeAiEventBus(): CreativeAiProgressEventBus | null {
  return installedBus
}

/** Slice D supplies the label-schema store without adding a second SSE subscriber. */
export function registerRelabelProgressConsumer(consumer: RelabelProgressConsumer): () => void {
  relabelConsumer = consumer
  relabelRegistrationVersion.value += 1
  return () => {
    if (relabelConsumer === consumer) {
      relabelConsumer = null
      relabelRegistrationVersion.value += 1
    }
  }
}

function payloadWorkspace(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null
  const value = (payload as Record<string, unknown>).workspace_id
  return typeof value === 'number' || typeof value === 'string' ? String(value) : null
}

/** Framework-light runtime: one classifier, one workspace fence and independent terminal sets. */
export function createLabellingProgressRuntime(
  deps: LabellingProgressRuntimeDeps,
): LabellingProgressRuntime {
  const latest = new Map<string, CreativeLabellingProgressEvent>()
  const seenTerminals = new Set<string>()
  const pending = new Set<Promise<void>>()
  const visibilityDocument = deps.document ?? globalThis.document
  let unsubscribeProgress: (() => void) | null = null
  let unsubscribeGap: (() => void) | null = null
  let hiddenWithAnalysis = false
  let hiddenWithRelabel = false

  function track(job: Promise<void>): void {
    pending.add(job)
    void job.finally(() => pending.delete(job))
  }

  function belongsToWorkspace(workspaceId: number | string): boolean {
    const selected = deps.workspaceId()
    return selected !== undefined && String(workspaceId) === String(selected)
  }

  function onProgress(raw: unknown): void {
    const frame = parseLabellingFrame(raw)
    if (!frame || !belongsToWorkspace(frame.workspace_id)) return
    const kind = classifyLabellingFrame(frame)
    const key = `${kind}:${frame.id}`
    if (!isNewerLabellingFrame(frame, latest.get(key))) return
    latest.set(key, frame)

    if (kind === 'analysis') deps.applyAnalysisProgress(frame)
    else deps.applyRelabelProgress(frame)

    if (!isTerminalLabellingFrame(frame)) return
    const terminalKey = `${key}:${frame.status}`
    if (seenTerminals.has(terminalKey)) return
    seenTerminals.add(terminalKey)

    if (kind === 'analysis') {
      track(deps.reloadAnalyses())
      if (frame.status === 'done') track(deps.refreshBalance())
      return
    }
    track(deps.reloadRelabel())
    if (frame.status === 'completed') track(deps.refreshBalance())
  }

  function recoverFromGap(payload: unknown): void {
    const workspace = payloadWorkspace(payload)
    if (workspace !== null && workspace !== String(deps.workspaceId())) return
    if (deps.isAnalysisActive()) track(deps.reloadAnalyses())
    if (deps.isRelabelActive()) track(deps.reloadRelabel())
  }

  function onVisibilityChange(): void {
    if (visibilityDocument?.visibilityState === 'hidden') {
      hiddenWithAnalysis = deps.isAnalysisActive()
      hiddenWithRelabel = deps.isRelabelActive()
      return
    }
    if (hiddenWithAnalysis) track(deps.reloadAnalyses())
    if (hiddenWithRelabel) track(deps.reloadRelabel())
    hiddenWithAnalysis = false
    hiddenWithRelabel = false
  }

  return {
    start: () => {
      if (unsubscribeProgress) return
      unsubscribeProgress = deps.bus.on('creative_labelling_progress', onProgress)
      unsubscribeGap = deps.bus.on('stream.gap', recoverFromGap)
      visibilityDocument?.addEventListener('visibilitychange', onVisibilityChange)
    },
    stop: () => {
      unsubscribeProgress?.()
      unsubscribeProgress = null
      unsubscribeGap?.()
      unsubscribeGap = null
      visibilityDocument?.removeEventListener('visibilitychange', onVisibilityChange)
      latest.clear()
      seenTerminals.clear()
      hiddenWithAnalysis = false
      hiddenWithRelabel = false
    },
    whenIdle: async () => {
      await Promise.all([...pending])
    },
  }
}

function productionRuntime(store: CreativeAiStore): LabellingProgressRuntime {
  if (!installedBus) throw new Error('Creative AI event bus must be registered before install')
  const session = useAccountSession()
  const creditGuard = useCreditGuard()
  return createLabellingProgressRuntime({
    bus: installedBus,
    workspaceId: () => session.workspaceId.value,
    isAnalysisActive: () => store.hasActiveRun,
    isRelabelActive: () => relabelConsumer?.active.value ?? false,
    applyAnalysisProgress: store.applyAnalysisProgress,
    applyRelabelProgress: (frame) => relabelConsumer?.applyProgress(frame),
    reloadAnalyses: store.reloadAnalyses,
    reloadRelabel: async () => relabelConsumer?.reload(),
    refreshBalance: creditGuard.refreshBalance,
  })
}

/** Install once for the app lifetime; session/workspace changes tear down stale subscriptions. */
export function installCreativeAiProgressLifecycle(): void {
  if (installedLifecycleStops.length > 0) return
  const session = useAccountSession()
  const store = useCreativeAiStore()
  const viewStore = useCreativeAiViewStore()
  const syncRuntime = (): void => {
    const shouldListen =
      session.status.value === 'ready' &&
      session.workspaceId.value !== undefined &&
      (store.hasActiveRun || relabelConsumer?.active.value === true)
    if (!shouldListen) {
      installedRuntime?.stop()
      installedRuntime = null
      return
    }
    if (installedRuntime) return
    installedRuntime = productionRuntime(store)
    installedRuntime.start()
  }

  const stopSessionWatch = watch(
    [session.status, session.workspaceId],
    ([status, workspaceId], previous) => {
      const changed = !previous || previous[0] !== status || previous[1] !== workspaceId
      if (!changed) return
      installedRuntime?.stop()
      installedRuntime = null
      store.resetState()
      if (previous?.[0] === 'ready' && status !== 'ready') viewStore.resetState(true)
      else viewStore.resetRuntime()
      relabelConsumer?.reset()
      syncRuntime()
    },
    { immediate: true, flush: 'sync' },
  )
  const stopActivityWatch = watch(
    [
      () => store.hasActiveRun,
      relabelRegistrationVersion,
      () => relabelConsumer?.active.value ?? false,
    ],
    syncRuntime,
    { immediate: true, flush: 'sync' },
  )
  installedLifecycleStops = [stopSessionWatch, stopActivityWatch]
}

export function resetCreativeAiProgressLifecycleForTest(): void {
  for (const stop of installedLifecycleStops) stop()
  installedLifecycleStops = []
  installedRuntime?.stop()
  installedRuntime = null
  installedBus = null
  relabelConsumer = null
  relabelRegistrationVersion.value = 0
}
